import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type CameraPreviewStatus =
  | 'off'
  | 'starting'
  | 'waiting-stream'
  | 'playing'
  | 'switching'
  | 'stopping'
  | 'error';

export type CameraPreviewState = {
  status: CameraPreviewStatus;
  robotId: string | null;
  leaseToken: string | null;
  errorMessage: string | null;
};

export type RobotCameraPreviewRemote = {
  representativeRobotId: string | null;
  setRepresentative: (robotId: string | null) => void;
  acquireRepresentativeCamera: () => Promise<string | null>;
  migrateRepresentativeCamera: (token: string) => Promise<boolean>;
  releaseCamera: (token: string) => Promise<void>;
  setRobotMicrophone: (robotId: string, enabled: boolean) => Promise<void>;
};

export type RobotCameraPreviewController = {
  state: CameraPreviewState;
  toggleCamera: (robotId: string) => Promise<void>;
  closeCamera: () => Promise<void>;
  retryCamera: () => Promise<void>;
  markStreamPlaying: (robotId: string) => void;
  markStreamWaiting: (robotId: string) => void;
  reportStreamError: (robotId: string, message: string) => void;
};

const INITIAL_STATE: CameraPreviewState = {
  status: 'off',
  robotId: null,
  leaseToken: null,
  errorMessage: null,
};

const errorMessageOf = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error) return error;
  return fallback;
};

export function useRobotCameraPreview(remote: RobotCameraPreviewRemote): RobotCameraPreviewController {
  const remoteRef = useRef(remote);
  remoteRef.current = remote;
  const mountedRef = useRef(true);
  const operationGenerationRef = useRef(0);
  const leaseTokenRef = useRef<string | null>(null);
  const leaseRobotIdRef = useRef<string | null>(null);
  const migrationTargetRef = useRef<string | null>(null);
  const [state, setState] = useState<CameraPreviewState>(INITIAL_STATE);
  const stateRef = useRef(state);

  const commitState = useCallback((next: CameraPreviewState | ((current: CameraPreviewState) => CameraPreviewState)) => {
    if (!mountedRef.current) return;
    setState((current) => {
      const resolved = typeof next === 'function' ? next(current) : next;
      stateRef.current = resolved;
      return resolved;
    });
  }, []);

  const closeCamera = useCallback(async () => {
    const generation = ++operationGenerationRef.current;
    migrationTargetRef.current = null;
    const robotId = leaseRobotIdRef.current;
    leaseRobotIdRef.current = null;
    const token = leaseTokenRef.current;
    leaseTokenRef.current = null;

    if (stateRef.current.status !== 'off') {
      commitState({
        status: 'stopping',
        robotId: stateRef.current.robotId,
        leaseToken: null,
        errorMessage: null,
      });
    }

    if (robotId) await remoteRef.current.setRobotMicrophone(robotId, false).catch(() => undefined);
    if (token) await remoteRef.current.releaseCamera(token);
    if (generation !== operationGenerationRef.current) return;
    commitState(INITIAL_STATE);
  }, [commitState]);

  const startCamera = useCallback(async (robotId: string, restart: boolean) => {
    const generation = ++operationGenerationRef.current;
    const previousToken = leaseTokenRef.current;
    const shouldAcquire = restart || !previousToken;
    migrationTargetRef.current = shouldAcquire ? null : robotId;

    commitState({
      status: shouldAcquire ? 'starting' : 'switching',
      robotId,
      leaseToken: restart ? null : previousToken,
      errorMessage: null,
    });

    try {
      if (restart && previousToken) {
        const previousRobotId = leaseRobotIdRef.current;
        leaseTokenRef.current = null;
        leaseRobotIdRef.current = null;
        if (previousRobotId) {
          await remoteRef.current.setRobotMicrophone(previousRobotId, false).catch(() => undefined);
        }
        await remoteRef.current.releaseCamera(previousToken);
        if (generation !== operationGenerationRef.current || !mountedRef.current) return;
      }

      remoteRef.current.setRepresentative(robotId);

      if (shouldAcquire) {
        const token = await remoteRef.current.acquireRepresentativeCamera();
        if (!token) throw new Error('表示できる代表ロボットがありません');
        if (generation !== operationGenerationRef.current || !mountedRef.current) {
          await remoteRef.current.releaseCamera(token);
          return;
        }
        leaseTokenRef.current = token;
        leaseRobotIdRef.current = robotId;
        await remoteRef.current.setRobotMicrophone(robotId, true);
        if (generation !== operationGenerationRef.current || !mountedRef.current) {
          leaseTokenRef.current = null;
          leaseRobotIdRef.current = null;
          await remoteRef.current.setRobotMicrophone(robotId, false).catch(() => undefined);
          await remoteRef.current.releaseCamera(token);
          return;
        }
        commitState({
          status: 'waiting-stream',
          robotId,
          leaseToken: token,
          errorMessage: null,
        });
        return;
      }

      const migrated = await remoteRef.current.migrateRepresentativeCamera(previousToken);
      if (generation !== operationGenerationRef.current || !mountedRef.current) return;
      if (!migrated) {
        const previousRobotId = leaseRobotIdRef.current;
        leaseTokenRef.current = null;
        leaseRobotIdRef.current = null;
        if (previousRobotId) {
          await remoteRef.current.setRobotMicrophone(previousRobotId, false).catch(() => undefined);
        }
        commitState({
          status: 'error',
          robotId,
          leaseToken: null,
          errorMessage: 'カメラの表示対象を切り替えられませんでした',
        });
        return;
      }
      const previousRobotId = leaseRobotIdRef.current;
      if (previousRobotId && previousRobotId !== robotId) {
        await remoteRef.current.setRobotMicrophone(previousRobotId, false).catch(() => undefined);
      }
      leaseRobotIdRef.current = robotId;
      await remoteRef.current.setRobotMicrophone(robotId, true);
      if (generation !== operationGenerationRef.current || !mountedRef.current) return;
      commitState({
        status: 'waiting-stream',
        robotId,
        leaseToken: previousToken,
        errorMessage: null,
      });
    } catch (error) {
      if (generation !== operationGenerationRef.current || !mountedRef.current) return;
      const heldToken = leaseTokenRef.current;
      const heldRobotId = leaseRobotIdRef.current;
      leaseTokenRef.current = null;
      leaseRobotIdRef.current = null;
      if (heldRobotId) {
        await remoteRef.current.setRobotMicrophone(heldRobotId, false).catch(() => undefined);
      }
      if (heldToken) await remoteRef.current.releaseCamera(heldToken);
      if (generation !== operationGenerationRef.current || !mountedRef.current) return;
      commitState({
        status: 'error',
        robotId,
        leaseToken: null,
        errorMessage: errorMessageOf(error, 'カメラを開始できませんでした'),
      });
    } finally {
      if (migrationTargetRef.current === robotId) migrationTargetRef.current = null;
    }
  }, [commitState]);

  const toggleCamera = useCallback(async (robotId: string) => {
    const current = stateRef.current;
    if (current.robotId === robotId && leaseTokenRef.current) {
      await closeCamera();
      return;
    }
    await startCamera(robotId, false);
  }, [closeCamera, startCamera]);

  const retryCamera = useCallback(async () => {
    const robotId = stateRef.current.robotId ?? remoteRef.current.representativeRobotId;
    if (!robotId) return;
    await startCamera(robotId, true);
  }, [startCamera]);

  const markStreamPlaying = useCallback((robotId: string) => {
    if (stateRef.current.robotId !== robotId || !leaseTokenRef.current) return;
    if (!['waiting-stream', 'playing', 'error'].includes(stateRef.current.status)) return;
    commitState((current) => current.robotId === robotId ? {
      ...current,
      status: 'playing',
      errorMessage: null,
    } : current);
  }, [commitState]);

  const markStreamWaiting = useCallback((robotId: string) => {
    if (stateRef.current.robotId !== robotId || !leaseTokenRef.current) return;
    if (stateRef.current.status !== 'playing') return;
    commitState((current) => current.robotId === robotId ? {
      ...current,
      status: 'waiting-stream',
      errorMessage: null,
    } : current);
  }, [commitState]);

  const reportStreamError = useCallback((robotId: string, message: string) => {
    if (stateRef.current.robotId !== robotId || !leaseTokenRef.current) return;
    if (!['waiting-stream', 'playing', 'error'].includes(stateRef.current.status)) return;
    commitState((current) => current.robotId === robotId ? {
      ...current,
      status: 'error',
      errorMessage: message,
    } : current);
  }, [commitState]);

  useEffect(() => {
    const representativeRobotId = remote.representativeRobotId;
    const token = leaseTokenRef.current;
    if (!representativeRobotId) {
      if (stateRef.current.status !== 'off') void closeCamera();
      return;
    }
    if (!token || leaseRobotIdRef.current === representativeRobotId || migrationTargetRef.current === representativeRobotId) return;

    const generation = ++operationGenerationRef.current;
    migrationTargetRef.current = representativeRobotId;
    commitState({
      status: 'switching',
      robotId: representativeRobotId,
      leaseToken: token,
      errorMessage: null,
    });

    void (async () => {
      try {
        const migrated = await remoteRef.current.migrateRepresentativeCamera(token);
        if (generation !== operationGenerationRef.current || !mountedRef.current) return;
        if (!migrated) {
          const previousRobotId = leaseRobotIdRef.current;
          leaseTokenRef.current = null;
          leaseRobotIdRef.current = null;
          if (previousRobotId) {
            await remoteRef.current.setRobotMicrophone(previousRobotId, false).catch(() => undefined);
          }
          commitState({
            status: 'error',
            robotId: representativeRobotId,
            leaseToken: null,
            errorMessage: 'カメラの表示対象を切り替えられませんでした',
          });
          return;
        }
        const previousRobotId = leaseRobotIdRef.current;
        if (previousRobotId && previousRobotId !== representativeRobotId) {
          await remoteRef.current.setRobotMicrophone(previousRobotId, false).catch(() => undefined);
        }
        leaseRobotIdRef.current = representativeRobotId;
        await remoteRef.current.setRobotMicrophone(representativeRobotId, true);
        if (generation !== operationGenerationRef.current || !mountedRef.current) return;
        commitState({
          status: 'waiting-stream',
          robotId: representativeRobotId,
          leaseToken: token,
          errorMessage: null,
        });
      } catch (error) {
        if (generation !== operationGenerationRef.current || !mountedRef.current) return;
        const previousRobotId = leaseRobotIdRef.current;
        leaseTokenRef.current = null;
        leaseRobotIdRef.current = null;
        if (previousRobotId) {
          await remoteRef.current.setRobotMicrophone(previousRobotId, false).catch(() => undefined);
        }
        await remoteRef.current.releaseCamera(token);
        if (generation !== operationGenerationRef.current || !mountedRef.current) return;
        commitState({
          status: 'error',
          robotId: representativeRobotId,
          leaseToken: null,
          errorMessage: errorMessageOf(error, 'カメラの表示対象を切り替えられませんでした'),
        });
      } finally {
        if (migrationTargetRef.current === representativeRobotId) migrationTargetRef.current = null;
      }
    })();
  }, [closeCamera, commitState, remote.representativeRobotId]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      operationGenerationRef.current += 1;
      const token = leaseTokenRef.current;
      const robotId = leaseRobotIdRef.current;
      leaseTokenRef.current = null;
      leaseRobotIdRef.current = null;
      migrationTargetRef.current = null;
      if (robotId) void remoteRef.current.setRobotMicrophone(robotId, false).catch(() => undefined);
      if (token) void remoteRef.current.releaseCamera(token);
    };
  }, []);

  return useMemo(() => ({
    state,
    toggleCamera,
    closeCamera,
    retryCamera,
    markStreamPlaying,
    markStreamWaiting,
    reportStreamError,
  }), [
    closeCamera,
    markStreamPlaying,
    markStreamWaiting,
    reportStreamError,
    retryCamera,
    state,
    toggleCamera,
  ]);
}
